import { useState } from "react";
import axios from "axios";
import { User, MapPin, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditShipmentForm = ({ shipment, customers, onSuccess, onCancel }) => {
  const token = localStorage.getItem("token");
  const axiosAuth = axios.create({
    headers: { Authorization: `Bearer ${token}` },
  });
  
  const [formData, setFormData] = useState({
    sender: {
      name: shipment.sender?.name || "",
      address: shipment.sender?.address || "",
      city: shipment.sender?.city || "",
      phone: shipment.sender?.phone || "",
    },
    receiver: {
      name: shipment.receiver?.name || "",
      address: shipment.receiver?.address || "",
      city: shipment.receiver?.city || "",
      phone: shipment.receiver?.phone || "",
    },
    origin: shipment.origin || "",
    destination: shipment.destination || "",
    weight: shipment.weight || "",
    volume: shipment.volume || "",
    description: shipment.description || "",
    estimated_delivery: shipment.estimated_delivery || "",
    customer_id: shipment.customer_id || "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (section, field, value) => {
    if (section) {
      setFormData((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        sender: formData.sender,
        receiver: formData.receiver,
        origin: formData.origin,
        destination: formData.destination,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        volume: formData.volume ? parseFloat(formData.volume) : null,
        description: formData.description || null,
        estimated_delivery: formData.estimated_delivery || null,
        customer_id: formData.customer_id || null,
      };
      await axiosAuth.put(`${API}/shipments/${shipment.id}`, payload);
      toast.success("Shipment updated successfully!");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update shipment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Tracking Number (Read-only) */}
      <div className="bg-slate-100 p-3 rounded-lg">
        <p className="text-xs uppercase tracking-widest text-slate-500">Tracking Number</p>
        <p className="font-mono font-bold text-slate-900">{shipment.tracking_number}</p>
      </div>

      {/* Sender */}
      <div>
        <h4 className="font-chivo font-bold text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
          <User className="w-4 h-4" /> Sender Information
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Name</Label>
            <Input
              value={formData.sender.name}
              onChange={(e) => handleChange("sender", "name", e.target.value)}
              required
              data-testid="edit-sender-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Phone</Label>
            <Input
              value={formData.sender.phone}
              onChange={(e) => handleChange("sender", "phone", e.target.value)}
              required
              data-testid="edit-sender-phone"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">Address</Label>
            <Input
              value={formData.sender.address}
              onChange={(e) => handleChange("sender", "address", e.target.value)}
              required
              data-testid="edit-sender-address"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">City</Label>
            <Input
              value={formData.sender.city}
              onChange={(e) => handleChange("sender", "city", e.target.value)}
              required
              data-testid="edit-sender-city"
            />
          </div>
        </div>
      </div>

      {/* Receiver */}
      <div>
        <h4 className="font-chivo font-bold text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Receiver Information
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Name</Label>
            <Input
              value={formData.receiver.name}
              onChange={(e) => handleChange("receiver", "name", e.target.value)}
              required
              data-testid="edit-receiver-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Phone</Label>
            <Input
              value={formData.receiver.phone}
              onChange={(e) => handleChange("receiver", "phone", e.target.value)}
              required
              data-testid="edit-receiver-phone"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">Address</Label>
            <Input
              value={formData.receiver.address}
              onChange={(e) => handleChange("receiver", "address", e.target.value)}
              required
              data-testid="edit-receiver-address"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">City</Label>
            <Input
              value={formData.receiver.city}
              onChange={(e) => handleChange("receiver", "city", e.target.value)}
              required
              data-testid="edit-receiver-city"
            />
          </div>
        </div>
      </div>

      {/* Shipment Details */}
      <div>
        <h4 className="font-chivo font-bold text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
          <Package className="w-4 h-4" /> Shipment Details
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Origin</Label>
            <Input
              value={formData.origin}
              onChange={(e) => handleChange(null, "origin", e.target.value)}
              placeholder="City, Country"
              required
              data-testid="edit-origin"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Destination</Label>
            <Input
              value={formData.destination}
              onChange={(e) => handleChange(null, "destination", e.target.value)}
              placeholder="City, Country"
              required
              data-testid="edit-destination"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Weight (lbs)</Label>
            <Input
              type="number"
              step="0.1"
              value={formData.weight}
              onChange={(e) => handleChange(null, "weight", e.target.value)}
              data-testid="edit-weight"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Volume (ft³)</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.volume}
              onChange={(e) => handleChange(null, "volume", e.target.value)}
              data-testid="edit-volume"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Est. Delivery</Label>
            <Input
              type="date"
              value={formData.estimated_delivery}
              onChange={(e) => handleChange(null, "estimated_delivery", e.target.value)}
              data-testid="edit-delivery-date"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange(null, "description", e.target.value)}
              placeholder="Package contents..."
              rows={2}
              data-testid="edit-description"
            />
          </div>
          {customers && customers.length > 0 && (
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Assigned Customer</Label>
              <Select 
                value={formData.customer_id || "none"} 
                onValueChange={(v) => handleChange(null, "customer_id", v === "none" ? "" : v)}
              >
                <SelectTrigger data-testid="edit-customer-select">
                  <SelectValue placeholder="Select customer..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No customer</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={loading}
          className="flex-1 h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest"
          data-testid="save-shipment-btn"
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="h-12 px-6"
          >
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default EditShipmentForm;
